/**
 * Background scroll lock for the full-screen demo overlay (FLO-108).
 *
 * Why this exists: the demo mounts a full-viewport `position: fixed` takeover
 * (`onboarding-sdk-react-ui` → DemoView), but the host page underneath stays
 * scrollable — nothing ever locked it. On mobile a stray swipe scrolls the
 * body; the browser's dynamic toolbar then resizes the visual viewport, so the
 * layout-viewport-anchored fixed overlay (and its top-right "open chat" toggle
 * at top:18/right:22) slides out of the visible/tappable area. The toggle
 * "stops opening the menu" once you've scrolled. Desktop is unaffected: there's
 * nothing to scroll and no dynamic toolbar.
 *
 * The lock pins `<body>` with `position: fixed; top: -<scrollY>` rather than
 * just `overflow: hidden`, because iOS Safari ignores `overflow: hidden` on the
 * body for touch scrolling. Unlock restores every property it touched and the
 * original scroll position. It is reference-counted so a duplicate/nested lock
 * (e.g. React StrictMode's double-invoked effects in dev) can't clobber the
 * saved state or leave the page stuck.
 *
 * Scope: call this ONLY for the demo overlay (a full-screen takeover). The
 * onboarding overlay is a transparent, pointer-events:none guidance layer and
 * must leave the customer's page scrollable.
 */

interface SavedScrollState {
  bodyPosition: string;
  bodyTop: string;
  bodyLeft: string;
  bodyRight: string;
  bodyWidth: string;
  bodyOverflow: string;
  bodyOverscroll: string;
  htmlOverflow: string;
  scrollY: number;
}

let lockCount = 0;
let saved: SavedScrollState | null = null;

function hasDom(): boolean {
  return typeof document !== 'undefined' && !!document.body && typeof window !== 'undefined';
}

/**
 * Lock background scrolling. Returns an idempotent unlock function — calling it
 * more than once is a no-op after the first. Safe to call with no DOM (SSR /
 * non-browser test env): returns a no-op.
 */
export function lockBodyScroll(): () => void {
  if (!hasDom()) return () => {};

  lockCount += 1;
  if (lockCount === 1) {
    const body = document.body;
    const html = document.documentElement;
    const scrollY = window.scrollY || window.pageYOffset || 0;

    saved = {
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyLeft: body.style.left,
      bodyRight: body.style.right,
      bodyWidth: body.style.width,
      bodyOverflow: body.style.overflow,
      bodyOverscroll: body.style.overscrollBehavior,
      htmlOverflow: html.style.overflow,
      scrollY,
    };

    // Pin the body in place at its current scroll offset. `position: fixed`
    // (not just overflow:hidden) is what actually stops iOS touch scrolling.
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    body.style.overscrollBehavior = 'none';
    html.style.overflow = 'hidden';
  }

  let released = false;
  return () => {
    if (released) return;
    released = true;
    unlockBodyScroll();
  };
}

/**
 * Decrement the lock and, when the last lock is released, restore the original
 * body/html styles and scroll position. Usually you call the function returned
 * by {@link lockBodyScroll} instead of this directly.
 */
export function unlockBodyScroll(): void {
  if (!hasDom() || lockCount === 0) return;

  lockCount -= 1;
  if (lockCount === 0 && saved) {
    const body = document.body;
    const html = document.documentElement;
    const { scrollY } = saved;

    body.style.position = saved.bodyPosition;
    body.style.top = saved.bodyTop;
    body.style.left = saved.bodyLeft;
    body.style.right = saved.bodyRight;
    body.style.width = saved.bodyWidth;
    body.style.overflow = saved.bodyOverflow;
    body.style.overscrollBehavior = saved.bodyOverscroll;
    html.style.overflow = saved.htmlOverflow;
    saved = null;

    // Restore the scroll position the lock froze (body was pinned at -scrollY).
    window.scrollTo(0, scrollY);
  }
}
